import { callGeotabAPI } from '../api/geotab-client';
import type { Device, DeviceStatusInfo, Group } from '../api/types';
import type { WatchdogRow, WatchdogStatus } from './watchdog-types';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS  = 24 * HOUR_MS;

/** Bucket a device by how long ago it last communicated. */
export function classify(lastComm: Date | null): WatchdogStatus {
  if (!lastComm) return 'notActive';                 // provisioned & active, but has never reported at all
  const age = Date.now() - lastComm.getTime();
  if (age < DAY_MS)     return 'ok';                 // < 24h
  if (age < 2 * DAY_MS) return 'offline1';           // 24–48h
  if (age < 3 * DAY_MS) return 'offline2';           // 48–72h
  return 'offline3';                                 // ≥ 72h
}

// Geotab auto-classifies every device into built-in "Asset Type" and "Powertrain and Fuel Type"
// groups (e.g. "Vehicle", "Gasoline or Petrol"), nested under the reserved GroupAssetInformationId
// group. These are Geotab-managed categories, not the fleet's own dispatch/region groups, so they're
// excluded when resolving a device's group — picking one up here would hide the actual organizational
// group a fleet manager expects to see. Ancestry (walked via the parent map below) is the authoritative
// check; the name list is a safety net for accounts where the fetched Group list omits `children`.
const SYSTEM_GROUP_ROOT_IDS = new Set(['GroupAssetInformationId']);

const SYSTEM_GROUP_NAMES = new Set([
  'asset information', 'asset type', 'powertrain and fuel type', 'internal combustion engine',
  'vehicle', 'trailer', 'heavy duty', 'heavy-duty', 'medium duty', 'medium-duty', 'light duty', 'light-duty',
  'flatbed', 'enclosed', 'refrigerated',
  'gasoline or petrol', 'diesel', 'propane or lpg', 'compressed natural gas (cng)',
  'liquefied natural gas (lng)', 'ethanol', 'flex fuel', 'biodiesel', 'unknown',
  'electric', 'hybrid', 'plug-in hybrid electric vehicle (phev)', 'battery electric vehicle (bev)',
  'fuel cell electric vehicle (fcev)', 'manually classified powertrain',
]);

function isSystemGroup(id: string, name: string, parentById: Map<string, string>): boolean {
  if (SYSTEM_GROUP_NAMES.has(name.trim().toLowerCase())) return true;
  let cur: string | undefined = id;
  const seen = new Set<string>();
  while (cur && !seen.has(cur)) {
    if (SYSTEM_GROUP_ROOT_IDS.has(cur)) return true;
    seen.add(cur);
    cur = parentById.get(cur);
  }
  return false;
}

/**
 * Lightweight connectivity snapshot for the Watchdog page.
 *
 * Deliberately fetches ONLY Device + DeviceStatusInfo + Group (three cheap, O(n) `Get` calls) —
 * no faults or status-data — so it can refresh every 30s without the cost of loadFleetData().
 * Mirrors the active-device filter used by loadFleetData (fleet-data.ts:77-81) so the same set
 * of in-service devices is monitored.
 */
export async function loadWatchdogData(): Promise<WatchdogRow[]> {
  const [allDevices, dsiList, groups] = await Promise.all([
    callGeotabAPI<Device[]>('Get', { typeName: 'Device' }),
    callGeotabAPI<DeviceStatusInfo[]>('Get', { typeName: 'DeviceStatusInfo' }),
    callGeotabAPI<Group[]>('Get', { typeName: 'Group' }),
  ]);

  // Active + provisioned only — identical idiom to loadFleetData().
  const now = new Date();
  const devices = allDevices.filter((d) => {
    if (d.activeTo && new Date(d.activeTo) <= now) return false;
    if (!d.serialNumber) return false;
    return true;
  });

  // Index DSI by device id (current-state record, one per device).
  const dsiById = new Map<string, DeviceStatusInfo>();
  dsiList.forEach((dsi) => {
    if (dsi.device?.id) dsiById.set(dsi.device.id, dsi);
  });

  // Prebuild group lookup + parent map so composition stays O(n) at 3000+ devices. Recurses into
  // `children` so both possible `Get: Group` shapes work — a flat list of every group, or a tree
  // of roots with descendants only reachable via nested `children`.
  const groupsById = new Map<string, Group>();
  const parentById = new Map<string, string>();
  function indexGroup(g: Group): void {
    groupsById.set(g.id, g);
    (g.children ?? []).forEach((child) => {
      parentById.set(child.id, g.id);
      indexGroup(child);
    });
  }
  groups.forEach(indexGroup);

  const rows: WatchdogRow[] = devices.map((device) => {
    const dsi = dsiById.get(device.id);
    const lastComm = dsi?.dateTime ? new Date(dsi.dateTime) : null;

    // Resolve the device's own (small) group list against the prebuilt map, skipping Geotab's
    // built-in asset-type/fuel-type categories to surface the fleet's real organizational group.
    let groupName = 'No Group';
    for (const dg of device.groups ?? []) {
      const g = groupsById.get(dg.id);
      if (!g || isSystemGroup(g.id, g.name, parentById)) continue;
      groupName = g.name;
      break;
    }

    return {
      id:              device.id,
      name:            device.name || device.serialNumber,
      serialNumber:    device.serialNumber,
      group:           groupName,
      model:           device.deviceType || 'Unknown',
      lastComm,
      offlineMs:       lastComm ? Date.now() - lastComm.getTime() : null,
      status:          classify(lastComm),
      isCommunicating: dsi?.isDeviceCommunicating ?? false,
    };
  });

  return rows;
}
